import React, { FC, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { EditIcon, PlusIcon, TrashBinIcon } from '../../../assets/icons/index';
import Button from '../../../components/button/index';
import Loader from '../../../components/loader/index';
import { TeamHooks, useModalState } from '../../../hooks/index';
import { TeamMember } from '../../../types/team.types';
import { DeleteConfirmModal } from '../delete-confirm-modal';
import { MemberAvatar } from '../member-avatar';
import { computePtoSummary, groupVacationsByMember } from '../pto.util';
import { MemberFormValues } from '../team.validation';
import { formatIsoDate } from '../team-date.util';
import { SelectField } from '../team-field';
import { MemberFormModal } from './member-form-modal';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

export const MembersTab: FC = () => {
  const { members, isLoading } = TeamHooks.useTeamMembers();
  const { vacations } = TeamHooks.useVacations();
  const { createMember, isPending: isCreating } = TeamHooks.useCreateTeamMember();
  const { updateMember, isPending: isUpdating } = TeamHooks.useUpdateTeamMember();
  const { deleteMember, isPending: isDeleting } = TeamHooks.useDeleteTeamMember();

  // Group once so PTO math is O(members + vacations), not O(members × vacations) inside the render map.
  const vacationsByMember = groupVacationsByMember(vacations);

  const [ptoYear, setPtoYear] = useState(CURRENT_YEAR);
  const [selectedMember, setSelectedMember] = useState<null | TeamMember>(null);
  const [memberToDelete, setMemberToDelete] = useState<null | TeamMember>(null);

  const { isOpen: isFormOpen, handleOpenModal, handleCloseModal } = useModalState({});

  const handleAdd = () => {
    setSelectedMember(null);
    handleOpenModal();
  };

  const handleEdit = (member: TeamMember) => {
    setSelectedMember(member);
    handleOpenModal();
  };

  const handleSubmit = async (values: MemberFormValues) => {
    try {
      if (selectedMember) {
        await updateMember({ id: selectedMember.id, ...values });
        toast.success('Team member updated');
      } else {
        await createMember(values);
        toast.success('Team member added');
      }
      handleCloseModal();
    } catch {
      // error toast handled by useAppMutation default error handling
    }
  };

  const handleConfirmDelete = async () => {
    if (!memberToDelete) return;

    try {
      await deleteMember({ id: memberToDelete.id });
      toast.success('Team member removed');
      setMemberToDelete(null);
    } catch {
      // error toast handled by useAppMutation default error handling
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm font-[550] text-gray-700">
          {members.length} {members.length === 1 ? 'person' : 'people'}
        </span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-[450] text-gray-500">PTO year</span>
            <SelectField
              className="w-[96px]"
              onChange={(value) => setPtoYear(Number(value))}
              options={YEAR_OPTIONS.map((year) => ({ label: String(year), value: String(year) }))}
              value={String(ptoYear)}
            />
          </div>
          <Button
            className="flex h-9 w-fit items-center gap-1 whitespace-nowrap rounded px-4 py-[6px] text-sm font-[550] text-white"
            onClick={handleAdd}
          >
            <PlusIcon className="size-4" iconColor="#ffffff" />
            Add person
          </Button>
        </div>
      </div>

      <div className="w-full overflow-x-auto rounded-lg border border-gray-100 bg-white">
        <table className="w-full min-w-[820px] border-collapse">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs font-[450] text-gray-500">
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3">Available PTO ({ptoYear})</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => {
              const pto = computePtoSummary(
                member,
                vacationsByMember.get(member.id) ?? [],
                ptoYear,
              );

              return (
                <tr
                  className="border-b border-gray-50 text-xs font-[450] text-gray-700 last:border-b-0"
                  key={member.id}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <MemberAvatar member={member} size={28} />
                      <span className="font-[550] text-gray-800">{member.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{member.role}</td>
                  <td className="px-4 py-3">{member.email}</td>
                  <td className="px-4 py-3">{formatIsoDate(member.startDate)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span
                        className={twMerge(
                          'text-sm font-[550]',
                          pto.remaining < 0 ? 'text-fireside-600' : 'text-gray-800',
                        )}
                      >
                        {pto.remaining} days left
                      </span>
                      <span className="text-xs font-[450] text-gray-400">
                        of {pto.allowance} · {pto.used} used
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        className="flex size-8 items-center justify-center rounded hover:bg-gray-50"
                        onClick={() => handleEdit(member)}
                        styleType="NONE"
                      >
                        <EditIcon className="size-4" iconColor="#475467" />
                      </Button>
                      <Button
                        className="flex size-8 items-center justify-center rounded hover:bg-fireside-50"
                        onClick={() => setMemberToDelete(member)}
                        styleType="NONE"
                      >
                        <TrashBinIcon className="size-4" iconColor="#D92D20" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <MemberFormModal
        isOpen={isFormOpen}
        isPending={isCreating || isUpdating}
        member={selectedMember}
        onDismiss={handleCloseModal}
        onSubmit={handleSubmit}
      />
      <DeleteConfirmModal
        description={`Remove ${memberToDelete?.name ?? 'this person'} from the team? This also deletes their logged vacations. This cannot be undone.`}
        isOpen={!!memberToDelete}
        isPending={isDeleting}
        onConfirm={handleConfirmDelete}
        onDismiss={() => setMemberToDelete(null)}
        title="Remove team member"
      />
    </div>
  );
};
